import { useCallback, useEffect, useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { sendEvent } from '@/utils/gtag'
import { submitContactForm } from '@/data/api/contact'
import useAuth from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

import SubmitSuccess from './components/submit-success'
import { contactSchema } from './contact.schema'
import {
  CONSULTATION_FORMAT_OPTIONS,
  FORM_TYPES,
  TROUBLE_OPTIONS,
} from './contact.types'

import type { TContactSchema } from './contact.schema'
import type { FormType } from './contact.types'
import { cn } from '@/lib/utils'
import { yupResolver } from '@/lib/yup-resolver'

const ContactForm = () => {
  const { currentUser } = useAuth()
  const router = useRouter()
  const [ formType, setFormType ] = useState<FormType | null>(null)
  const [ isSubmitting, setSubmitting ] = useState(false)
  const [ isSubmitted, setSubmitted ] = useState(false)

  const form = useForm<TContactSchema>({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      formType: FORM_TYPES.consultation,
      email: '',
      firstName: '',
      subject: '',
      description: '',
      troubleType: '',
      consultationFormat: '',
    },
  })

  // ログインユーザーの情報を自動入力
  useEffect(() => {
    if (currentUser) {
      form.setValue('email', currentUser.email || '')
      form.setValue('firstName', currentUser.name || '')
    }
  }, [ currentUser, form ])

  // フォームタイプ変更時の処理
  const handleFormTypeChange = useCallback(
    (value: FormType) => {
      setFormType(value)
      form.setValue('formType', value)
      // フォーム切り替え時に専用フィールドをクリア
      if (value === FORM_TYPES.consultation) {
        form.setValue('troubleType', '')
        form.setValue('consultationFormat', '')
      }
      else {
        form.setValue('subject', '')
      }
      form.clearErrors()
    },
    [ form ],
  )

  // クエリパラメータからカテゴリを初期化
  useEffect(() => {
    if (!router.isReady) return
    const category = router.query.category as string | undefined
    if (category === FORM_TYPES.musicSupport && !formType) {
      handleFormTypeChange(FORM_TYPES.musicSupport)
    }
  }, [ router.isReady, router.query.category, formType, handleFormTypeChange ])

  const onSubmit = useCallback(
    async (values: TContactSchema) => {
      setSubmitting(true)

      try {
        // music-support の場合、troubleType を subject にマッピング
        const submitValues = values.formType === FORM_TYPES.musicSupport
          ? { ...values, subject: values.troubleType || '' }
          : values
        const result = await submitContactForm(submitValues)

        if ('error' in result) {
          toast.error(
            result.error || '送信に失敗しました。時間をおいて再度お試しください。',
          )
          return
        }

        setSubmitted(true)
        window.scrollTo(0, 0)
        sendEvent({
          event: 'support_contact_submit',
          form_type: values.formType,
          email: values.email,
          name: values.firstName || undefined,
          subject: submitValues.subject,
          description: values.description,
          trouble_type: values.troubleType || undefined,
          consultation_format: values.consultationFormat || undefined,
        })
        toast.success('お問い合わせを送信しました')
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('Submit error:', error)
        toast.error('エラーが発生しました。時間をおいて再度お試しください。')
      }
      finally {
        setSubmitting(false)
      }
    },
    [],
  )

  if (isSubmitted) {
    return <SubmitSuccess />
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* フォームタイプ選択 - カード形式 */}
        <div className="space-y-4 pb-7">
          <p className="text-base tracking-wider">
            お問い合わせ内容を選択してください
          </p>
          <div className="flex gap-5">
            {/* ご相談窓口カード */}
            <button
              type="button"
              aria-pressed={formType === FORM_TYPES.consultation}
              onClick={() => handleFormTypeChange(FORM_TYPES.consultation)}
              className={cn(
                'flex flex-1 flex-col items-center gap-2 rounded-xl border-2 bg-background px-4 py-2 text-center transition-all sm:px-10 sm:py-8',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                formType === FORM_TYPES.consultation
                  ? 'border-primary bg-primary/5 dark:bg-primary/40'
                  : 'border-border hover:border-[#670fff]/50',
              )}
            >
              <div className="order-2 flex min-h-[40px] flex-col justify-center sm:order-1 sm:min-h-0">
                <p className="text-primary mb-0 text-base leading-tight font-medium tracking-wider">
                  ご相談窓口
                </p>
              </div>
              <div className="relative order-1 h-24 w-24 sm:order-2 sm:h-32 sm:w-32">
                <Image
                  src="/images/contact/consultation-icon.png"
                  alt="ご相談窓口"
                  fill
                  className="object-contain"
                />
              </div>
              <p className="text-muted-foreground order-3 mb-0 text-sm leading-relaxed">
                サービス全般に関するご質問・お問い合わせはこちらから
              </p>
            </button>

            {/* 音楽制作のお困りごとカード */}
            <button
              type="button"
              aria-pressed={formType === FORM_TYPES.musicSupport}
              onClick={() => handleFormTypeChange(FORM_TYPES.musicSupport)}
              className={cn(
                'flex flex-1 flex-col items-center gap-2 rounded-xl border-2 bg-background px-4 pt-2 pb-4 text-center transition-all sm:px-10 sm:py-8',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                formType === FORM_TYPES.musicSupport
                  ? 'border-primary bg-primary/5 dark:bg-primary/40'
                  : 'border-border hover:border-[#670fff]/50',
              )}
            >
              <div className="order-2 flex min-h-[40px] flex-col justify-center sm:order-1 sm:min-h-0">
                <p className="text-pink mb-0 text-base leading-tight font-medium tracking-wider">
                  音楽制作の
                  <br className="sm:hidden" />
                  お困りごと
                </p>
              </div>
              <div className="relative order-1 h-24 w-24 sm:order-2 sm:h-32 sm:w-32">
                <Image
                  src="/images/contact/music-support-icon.png"
                  alt="音楽制作のお困りごと"
                  fill
                  className="object-contain"
                />
              </div>
              <p className="text-muted-foreground order-3 mb-0 text-sm leading-relaxed">
                音楽制作の始め方やご依頼先に迷ったらこちらから
              </p>
            </button>
          </div>
        </div>

        {/* フォームタイプ選択後に残りのフィールドを表示 */}
        {
          formType && (
            <>
              {/* メールアドレス・お名前（未ログイン時のみ表示） */}
              {!currentUser && (
                <>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">メールアドレス</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="example@email.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-base">お名前</FormLabel>
                          <span className="text-muted-foreground text-xs">※任意</span>
                        </div>
                        <FormControl>
                          <Input type="text" placeholder="お名前" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {/* 音楽制作フォーム専用フィールド */}
              {formType === FORM_TYPES.musicSupport && (
                <>
                  {/* 何かお困りですか？ */}
                  <FormField
                    control={form.control}
                    name="troubleType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>お困りごとの種類</FormLabel>
                        <FormDescription className="text-ghost text-sm">
                          制作に関するお困りごとをお知らせください。
                        </FormDescription>
                        <Select
                          value={field.value || ''}
                          onValueChange={field.onChange}
                        >
                          <FormControl className="bg-background w-full">
                            <SelectTrigger>
                              <SelectValue placeholder="ご相談内容を選択してください" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TROUBLE_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {/* 件名 - 音楽制作フォーム以外で表示 */}
              {formType !== FORM_TYPES.musicSupport && (
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">件名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="件名を入力してください" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* 説明 */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base">お困りごと</FormLabel>
                    <FormDescription className="text-ghost text-sm">
                      音楽制作に関してお困りの内容をお知らせください。運営スタッフが数日以内にご連絡いたします。
                    </FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="お問い合わせ内容を詳しくお書きください"
                        className="bg-background min-h-[200px] resize-y"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {formType === FORM_TYPES.musicSupport && (
                <>
                  {/* ご相談の形式 - ラジオボタン */}
                  <FormField
                    control={form.control}
                    name="consultationFormat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="mb-3 text-base">ご希望の相談形式</FormLabel>
                        <FormControl>
                          <div className="flex flex-col gap-2">
                            {CONSULTATION_FORMAT_OPTIONS.map((option) => (
                              <label
                                key={option}
                                className="mb-0 flex cursor-pointer items-center gap-2"
                              >
                                <input
                                  type="radio"
                                  name="consultationFormat"
                                  value={option}
                                  checked={field.value === option}
                                  onChange={() => field.onChange(option)}
                                  className="accent-primary size-4"
                                />
                                <span className="text-base font-normal tracking-wider">{option}</span>
                              </label>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {/* 送信ボタン */}
              <div className="flex justify-center pt-6">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="h-14 w-56"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" />
                  ) : (
                    '送信'
                  )}
                </Button>
              </div>
            </>
          )
        }
      </form >
    </Form >
  )
}

export default ContactForm
